import React, { useEffect, useState } from 'react';
import { ExternalLink, Eye, Loader2, Save, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  buildTaskTypeInstructionsViewerPath,
  getTaskTypeInstructionsHref,
  parseTaskTypeInstructionsDraft,
} from '../../taskTypes/instructions';
import { TaskTypeInstructions, TaskTypeInstructionsKind } from '../../types';

export function InstructionsEditor({
  taskTypeId,
  current,
  loading,
  saving,
  onSave,
}: {
  taskTypeId: string;
  current: TaskTypeInstructions | null;
  loading: boolean;
  saving: boolean;
  onSave: (instructions: TaskTypeInstructions | null) => Promise<void>;
}) {
  const [kind, setKind] = useState<TaskTypeInstructionsKind>(current?.kind ?? 'google-doc-url');
  const [value, setValue] = useState<string>(current?.value ?? '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setKind(current?.kind ?? 'google-doc-url');
    setValue(current?.value ?? '');
  }, [current?.kind, current?.value]);

  const href = current ? getTaskTypeInstructionsHref(taskTypeId, current) : null;

  const handleSave = async () => {
    const parsed = parseTaskTypeInstructionsDraft(kind, value);
    if ('error' in parsed) {
      setError(parsed.error);
      return;
    }
    setError(null);
    try {
      await onSave(parsed);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save instructions.');
    }
  };

  const handleClear = async () => {
    setError(null);
    try {
      await onSave(null);
      setKind('google-doc-url');
      setValue('');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to clear instructions.');
    }
  };

  const tabClass = (active: boolean) =>
    `rounded px-3 py-1.5 transition-colors ${
      active ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-50'
    }`;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900">Task instructions</h2>
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
        ) : (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
            {current ? (current.kind === 'markdown' ? 'Markdown' : 'Google Doc') : 'No instructions'}
          </span>
        )}
      </div>

      <div
        className="mt-3 grid w-full grid-cols-2 rounded-md border border-gray-300 bg-white p-0.5 text-sm"
        role="tablist"
        aria-label="Instruction source"
      >
        <button
          type="button"
          role="tab"
          aria-selected={kind === 'google-doc-url'}
          onClick={() => setKind('google-doc-url')}
          className={tabClass(kind === 'google-doc-url')}
        >
          Google Doc URL
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={kind === 'markdown'}
          onClick={() => setKind('markdown')}
          className={tabClass(kind === 'markdown')}
        >
          Markdown
        </button>
      </div>

      <div className="mt-3">
        <label className="mb-1 block text-xs font-medium text-gray-700">
          {kind === 'google-doc-url' ? 'Google Doc URL' : 'Markdown instructions'}
        </label>
        {kind === 'google-doc-url' ? (
          <input
            type="url"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="https://docs.google.com/document/d/..."
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        ) : (
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={14}
            placeholder="# Instructions&#10;&#10;Paste the markdown experts should follow."
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        )}
      </div>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        {href && current ? (
          current.kind === 'markdown' ? (
            <Link
              to={buildTaskTypeInstructionsViewerPath(taskTypeId)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900"
            >
              <Eye className="h-3.5 w-3.5" />
              View saved
            </Link>
          ) : (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open saved
            </a>
          )
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleClear}
            disabled={saving || !current}
            className="inline-flex min-h-[36px] items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Clear
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex min-h-[36px] items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-hover disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
